import React from "react";
import { useTranslation } from "react-i18next";

import { Stack, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { MeasurementIcon } from "../../icons/MeasurementIcon";
import { MeasurementAccessory, MeasurementType } from "../../shared/enums/Result";

import { MultilevelDropdown, MultilevelDropdownOption } from "../../shared/MultilevelDropdown";
import { ValidationMessage } from "../../shared/ValidationMessage";

interface Props {
  selectedMeasurementType: MeasurementType | undefined;
  selectedAccessory: MeasurementAccessory | undefined;
  onChange: (measurementType?: MeasurementType, measurementAccessory?: MeasurementAccessory) => void;
  invalid?: boolean;
  invalidMessage?: string;
  className?: string;
  disabled?: boolean;
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    position: "relative",
  },
  dropdownItem: {
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
    padding: theme.spacing(1),
  },
  selectedItemLabels: {
    width: "100%",
  },
  selectedIcon: {
    height: "2em",
    width: "2em",
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(3),
  },
  icon: {
    marginTop: "1px",
    marginRight: theme.spacing(2),
    height: "1em",
    width: "1em",
  },
  measurementType: {
    fontSize: "1.2em",
  },
  validationMessage: {
    position: "absolute",
    top: "100%",
    left: 0,
    marginTop: "5px",
    zIndex: 1,
  },
}));

export const MeasurementTypeDropdown = React.forwardRef((props: Props, ref: React.Ref<unknown>) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const options: MultilevelDropdownOption<MeasurementType, MeasurementAccessory>[] = Object.values(MeasurementType).map(
    (measurementType) => ({
      label: t("results.measurementType", { measurementType: `$t(MeasurementTypes.${measurementType} )` }),
      value: measurementType,
      children:
        measurementType === MeasurementType.Ultra
          ? Object.values(MeasurementAccessory).map((measurementAccessory) => ({
              label: t(`UltrasonicAccessory.${measurementAccessory}`),
              value: measurementAccessory,
            }))
          : undefined,
    }),
  );

  const selectedMeasurementTypeTemplate = (
    selectedMeasurementType: MeasurementType | undefined,
    selectedMeasurementAccessory: MeasurementAccessory | undefined,
  ) => {
    if (selectedMeasurementType) {
      return (
        <>
          <MeasurementIcon height="1.25em" width="1.25em" mr={2} measurementType={selectedMeasurementType} />
          <Stack direction="column">
            <Typography variant="subtitle2" fontSize="1em" className={classes.measurementType}>
              {t("results.measurementType", { measurementType: `$t(MeasurementTypes.${selectedMeasurementType} )` })}
            </Typography>

            {selectedMeasurementAccessory && (
              <Typography variant="subtitle2" color="textSecondary" component="span">
                {t(`UltrasonicAccessory.${selectedMeasurementAccessory}`)}
              </Typography>
            )}
          </Stack>
        </>
      );
    } else return t("MeasurementTypes.selectMeasurementType");
  };

  const measurementTypeOptionTemplate = (option: MeasurementType) => {
    return (
      <span className={classes.dropdownItem}>
        <MeasurementIcon className={classes.icon} measurementType={option} />
        {t("results.measurementType", { measurementType: `$t(MeasurementTypes.${option} )` })}
      </span>
    );
  };

  const measurementAccessoryOptionTemplate = (option: MeasurementAccessory) => {
    return <span className={classes.dropdownItem}>{t(`UltrasonicAccessory.${option}`)}</span>;
  };

  function handleSelect(value?: MeasurementType, subValue?: MeasurementAccessory) {
    props.onChange(value, subValue);
  }

  return (
    <span className={classes.root}>
      <MultilevelDropdown
        disabled={props.disabled}
        options={options}
        onChange={handleSelect}
        selectedOption={props.selectedMeasurementType}
        selectedSubOption={props.selectedAccessory}
        selectedItemTemplate={selectedMeasurementTypeTemplate}
        optionTemplate={measurementTypeOptionTemplate}
        submenuOptionTemplate={measurementAccessoryOptionTemplate}
        invalid={props.invalid}
        className={props.className}
      />
      {props.invalid && props.invalidMessage && (
        <div className={classes.validationMessage}>
          <ValidationMessage invalidMessage={props.invalidMessage} validationType="error" showIcon />
        </div>
      )}
    </span>
  );
});
